import React from 'react';
import { compactMoney, fullMoney } from '../format';

const TILE = 'rounded-xl p-4 flex flex-col gap-1.5 bg-muted/30';
const TILE_LABEL = 'font-label text-[10px] uppercase tracking-[0.06em] text-muted-foreground';
const FIGURE = 'font-light tracking-[-0.02em] tabular-nums text-[22px]';

export const COVERAGE = {
  comfortable: { label: 'Cash flow covers it comfortably', tone: 'text-teal-ink', bar: 'bg-[hsl(var(--chart-2))]' },
  tight: { label: 'Cash flow covers it, just', tone: 'text-amber-ink', bar: 'bg-[hsl(var(--amber))]' },
  short: { label: 'Cash flow falls short of the payments', tone: 'text-coral-ink', bar: 'bg-[hsl(var(--coral))]' },
};

const pct = (rate, digits = 1) => `${Number((rate * 100).toFixed(digits))}%`;

// Financing Scenario (PRD §6.3, Phase 2): how a buyer could pay for the
// business. Key figure is the monthly payment; tiles for the down payment,
// loan and rate; then debt service coverage against the lender's minimum.
export default function FinancingScenarioBody({ artifact, data }) {
  const coverage = COVERAGE[data.coverage];
  // Bar scale: 0x to twice the lender's minimum, the minimum in the middle.
  const scale = (data.min_dscr || 1.25) * 2;
  const fill = data.dscr ? `${Math.max(4, Math.min(100, (data.dscr / scale) * 100))}%` : '0%';

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <span className="text-[28px] font-light tracking-[-0.02em] tabular-nums">
          {fullMoney(data.monthly_payment)}
          <span className="ml-1.5 text-[15px] text-muted-foreground tracking-normal">a month</span>
        </span>
        <span className="text-[14px] text-muted-foreground tabular-nums">
          {data.loan_term_years} years · {fullMoney(data.total_interest)} interest in total
          {data.active === false ? ' · scenario' : ''}
        </span>
      </div>
      {artifact.summary && <p className="m-0 text-[15px] leading-[1.6] text-foreground/[0.88]">{artifact.summary}</p>}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2.5">
        <div className={TILE}>
          <span className={TILE_LABEL}>Price</span>
          <span className={FIGURE}>{compactMoney(data.purchase_price)}</span>
        </div>
        <div className={TILE}>
          <span className={TILE_LABEL}>Down payment</span>
          <span className={FIGURE}>{compactMoney(data.down_payment)}</span>
          <span className="font-label text-[11px] text-muted-foreground">{pct(data.down_payment_pct || 0, 0)}</span>
        </div>
        <div className={TILE}>
          <span className={TILE_LABEL}>Loan</span>
          <span className={FIGURE}>{compactMoney(data.loan_amount)}</span>
          <span className="font-label text-[11px] text-muted-foreground">at {pct(data.interest_rate || 0, 2)}</span>
        </div>
      </div>
      {coverage && (
        <div className="flex flex-col gap-2" data-testid="debt-coverage">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <span className={`text-[14px] font-medium ${coverage.tone}`}>{coverage.label}</span>
            <span className="font-label text-[11px] text-muted-foreground tabular-nums">
              {Number(data.dscr).toFixed(2)}x · lenders want {data.min_dscr}x
            </span>
          </div>
          <div
            role="img"
            aria-label={`Debt service coverage ${Number(data.dscr).toFixed(2)} times, lenders want ${data.min_dscr} times`}
            className="relative h-1.5 rounded-full bg-border"
          >
            <div className={`h-full rounded-full ${coverage.bar}`} style={{ width: fill }} />
            <div className="absolute -top-1 h-3.5 w-px bg-foreground/50 left-1/2" />
          </div>
        </div>
      )}
    </div>
  );
}
